import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '@components-clean/core/card';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { YesNoCell, DateCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { TagDto } from '@interfaces-clean/tags.interface';
import AddTagDialog from '@pages-clean/subject-monitoring/components/dialogs/add-tag-dialog';
import DeleteTagDialog from '@pages-clean/subject-monitoring/components/dialogs/delete-tag-dialog';
import EditTagDialog from '@pages-clean/subject-monitoring/components/dialogs/edit-tag-dialog';
import SubjectMonitoringService from '@pages-clean/subject-monitoring/subject-hub/subject-monitoring.service';
import { useBoolean } from '@hooks/index';
import { TagAPI } from '@apis/bff-api/tag';

interface TagGridProps {
    canEdit: boolean;
    studyRef: string;
    timestamp?: string;
}

interface Tag {
    label: string;
    active: boolean;
}

const SubjectMonitoringTagGrid = ({ canEdit, studyRef, timestamp }: TagGridProps) => {
    const { data, isMutating, trigger } = TagAPI.useGetAllTags(studyRef, timestamp);
    const [selectedItem, setSelectedItem] = useState<TagDto | undefined>(undefined);
    const [tagToEdit, setTagToEdit] = useState<TagDto | undefined>(undefined);
    const [existingTags, setExistingTags] = useState<Tag[]>([]);
    const { TooltipCell } = createGridCustomCells();
    const { t: tagGridTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });

    const showAddDialog = useBoolean(false);
    const showEditDialog = useBoolean(false);
    const showDeleteDialog = useBoolean(false);

    const showAdd = () => {
        showAddDialog.setTrue();
        showEditDialog.setFalse();
        showDeleteDialog.setFalse();
    };

    const showEdit = () => {
        showAddDialog.setFalse();
        showEditDialog.setTrue();
        showDeleteDialog.setFalse();
    };

    const showDelete = () => {
        showAddDialog.setFalse();
        showEditDialog.setFalse();
        showDeleteDialog.setTrue();
    };

    const closeAllDialogs = () => {
        showAddDialog.setFalse();
        showEditDialog.setFalse();
        showDeleteDialog.setFalse();
    };

    const reloadGrid = (selectLatestItem?: boolean) => {
        trigger()
            .then((loadedData) => {
                loadedData = loadedData.sort((a, b) =>
                    (a.label as string).localeCompare(b.label as string, undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    }),
                );
                const existing: Tag[] = loadedData.map((d) => {
                    return {
                        label: d.label!,
                        active: d.active!,
                    };
                });
                setExistingTags(existing);
                if (selectLatestItem === true) {
                    setSelectedItem(loadedData[loadedData.length - 1]);
                }
            })
            .catch((err) => window.console.log(err));
    };

    useEffect(() => {
        const subscribeDefault = SubjectMonitoringService.onRefreshManageTagGrid().subscribe((refreshData) => {
            if (refreshData) {
                reloadGrid(false);
            }
        });

        reloadGrid();

        return () => subscribeDefault.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const mapTags = (updatedData: TagDto[]) => {
        return updatedData.map((d) => {
            return {
                label: d.label!,
                active: d.active!,
            };
        }) as Tag[];
    };

    const refreshGrids = () => {
        SubjectMonitoringService.refreshTagGrid(true);
        SubjectMonitoringService.refreshSubjectHubGrid(true);
    };

    const onSubmitHandler = async (selectLastItem: boolean) => {
        closeAllDialogs();
        await trigger()
            .then((updatedData) => {
                // Last item will be the new one, as data is in ID order ascending.
                if (selectLastItem) setSelectedItem(updatedData[updatedData.length - 1]);
                const existing = mapTags(updatedData);
                setExistingTags(existing);
            })
            .catch((err) => window.console.log(err));
        refreshGrids();
    };

    const onSubmitEditHandler = async () => {
        closeAllDialogs();
        await trigger()
            .then((updatedData) => {
                // Select the edited tag
                if (tagToEdit?.id) {
                    const item = updatedData.find((x) => x.id === tagToEdit.id);
                    if (item) {
                        setSelectedItem(item);
                    }
                }
                const existing = mapTags(updatedData);
                setExistingTags(existing);
            })
            .catch((err) => window.console.log(err));
        refreshGrids();
    };

    const onDeleteSubmitHandler = async () => {
        setSelectedItem(undefined);
        await onSubmitHandler(false);
    };

    const deleteTagRow = (dataItem: unknown) => {
        const tagDto = dataItem as TagDto;
        setTagToEdit(tagDto);
        showDelete();
    };

    const editTagRow = (dataItem: unknown) => {
        const tagDto = dataItem as TagDto;
        setTagToEdit(tagDto);
        showEdit();
    };

    const columns = useMemo(() => {
        const tooltipCellTwoLine = TooltipCell({
            isCsv: false,
            maxLines: 2,
        });

        const cols: GridColumnProps[] = [
            {
                id: 'recordId',
                field: 'recordId',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.recordId'),
                width: 1,
                minWidth: 140,
                maxLength: 30,
                cells: { data: tooltipCellTwoLine },
                filter: 'numeric',
            },
            {
                id: 'uses',
                field: 'uses',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.uses'),
                width: 1,
                minWidth: 140,
                maxLength: 30,
                cells: { data: tooltipCellTwoLine },
                filter: 'numeric',
            },
            {
                id: 'label',
                field: 'label',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.label'),
                width: 1,
                minWidth: 140,
                maxLength: 30,
                cells: { data: tooltipCellTwoLine },
            },
            {
                id: 'active',
                field: 'active',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.active'),
                width: 1,
                minWidth: 140,
                cells: { data: YesNoCell },
                filter: 'boolean',
            },
            {
                id: 'createdByName',
                field: 'createdByName',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.createdBy'),
                width: 1,
                minWidth: 140,
                cells: { data: tooltipCellTwoLine },
            },
            {
                id: 'created',
                field: 'created',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.createdOn'),
                width: 1,
                minWidth: 140,
                cells: { data: DateCell },
                filter: 'date',
            },

            {
                id: 'updatedByName',
                field: 'updatedByName',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.updatedBy'),
                width: 1,
                minWidth: 140,
                cells: { data: tooltipCellTwoLine },
            },
            {
                id: 'updatedDate',
                field: 'updatedDate',
                title: tagGridTranslation('manageTagsGrid.columnsTitles.updatedOn'),
                width: 1,
                minWidth: 140,
                cells: { data: DateCell },
                filter: 'date',
            },
        ];

        return cols;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    if (!data) {
        return (
            <Card className="placeholder-card" data-testid="grid" title={tagGridTranslation('manageTagsGrid.title')}>
                {isMutating ? (
                    <Loading />
                ) : (
                    <InfoField message={tagGridTranslation('emptyMessage')} type="Info" layout="center" />
                )}
            </Card>
        );
    }

    let menuActions: RowMenuAction[] | undefined;
    if (canEdit) {
        menuActions = [
            {
                id: 1,
                label: tagGridTranslation('manageTagsGrid.actions.editLabel'),
                onClick: editTagRow,
                icon: ['far', 'pen-to-square'],
            },
            {
                id: 2,
                label: tagGridTranslation('manageTagsGrid.actions.deleteLabel'),
                onClick: deleteTagRow,
                icon: ['fas', 'trash-xmark'],
                colorProp: 'Red',
            },
        ];
    }

    return (
        <>
            {showAddDialog.value && (
                <AddTagDialog
                    studyRef={studyRef}
                    onClose={closeAllDialogs}
                    onSubmit={() => onSubmitHandler(true)}
                    existingTags={existingTags}
                />
            )}
            {showEditDialog.value && (
                <EditTagDialog
                    tagId={tagToEdit?.id}
                    studyRef={studyRef}
                    onClose={closeAllDialogs}
                    onSubmit={() => onSubmitEditHandler()}
                    existingTags={existingTags}
                    tagLabel={tagToEdit?.label}
                    isActive={tagToEdit?.active}
                    activeInstances={tagToEdit?.uses}
                />
            )}
            {showDeleteDialog.value && (
                <DeleteTagDialog
                    studyRef={studyRef}
                    tagId={tagToEdit?.id}
                    activeInstances={tagToEdit?.uses}
                    onClose={closeAllDialogs}
                    onSubmit={() => onDeleteSubmitHandler()}
                />
            )}
            <Grid
                dataItemKey="id"
                title={tagGridTranslation('manageTagsGrid.title')}
                maxHeight="auto"
                testDataId="all-tags-grid"
                data={data}
                columns={columns}
                loading={isMutating}
                useLoadingOverlay
                rowReorder={false}
                gridDownloadDisabled
                gridSettingsMenuDisabled
                addCallback={showAdd}
                canEdit={canEdit}
                selectable={{ enabled: true, mode: 'single' }}
                defaultSelectedItem={selectedItem}
                rowMenuActions={menuActions}
                showAddIcon={canEdit}
                scrollable="virtual"
            />
        </>
    );
};

export default SubjectMonitoringTagGrid;
